import { useEffect, useState } from "react";
import SatAPI from "../../_services/sat";
import toastService from "../../_services/toastService";

const api = SatAPI();

const fetchPrefsMaps = async (submissionId) => {
  const [ola, phase, task] = await Promise.all([
    api.getOlaPreferencesBySubmissionId(submissionId).catch(() => []),
    api.getPhasePreferencesBySubmissionId(submissionId).catch(() => []),
    api.getTaskPreferencesBySubmissionId(submissionId).catch(() => []),
  ]);
  return {
    olaPrefsMap: Object.fromEntries((ola || []).map(p => [p.olaId, p])),
    phasePrefsMap: Object.fromEntries((phase || []).map(p => [p.phaseId, p])),
    taskPrefsMap: Object.fromEntries((task || []).map(p => [p.taskId, p])),
  };
};

const upsertPref = async (pref, payload, createFn, updateFn, setMap, key) => {
  if (pref.id) {
    await updateFn(pref.id, { id: pref.id, ...payload });
  } else {
    const created = await createFn(payload);
    if (created?.id) {
      setMap(prev => ({ ...prev, [key]: { ...prev[key], id: created.id } }));
    }
  }
};

export default function useHoewistSubmission() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [teacher, setTeacher] = useState(null);
  const [programmes, setProgrammes] = useState([]);
  const [allOLAs, setAllOLAs] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [newTasks, setNewTasks] = useState([]);
  const [submission, setSubmission] = useState(null);

  const [academicYears, setAcademicYears] = useState([]);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);
  const [allSubmissions, setAllSubmissions] = useState([]);

  const [noAcademicYear, setNoAcademicYear] = useState(false);
  const [hoewistOpen, setHoewistOpen] = useState(false);

  const [olaPrefs, setOlaPrefs] = useState({});
  const [phasePrefs, setPhasePrefs] = useState({});
  const [taskPrefs, setTaskPrefs] = useState({});
  const [unsaved, setUnsaved] = useState(false);

  const prefillFromPreviousSubmission = async (sub, subs, sortedYears, collegaId) => {
    const prevSub = subs
      .filter(s => s.id !== sub.id)
      .sort((a, b) => {
        const yearA = sortedYears.find(y => y.id === a.academicYearId)?.year ?? "";
        const yearB = sortedYears.find(y => y.id === b.academicYearId)?.year ?? "";
        return yearB.localeCompare(yearA);
      })[0] ?? null;

    if (!prevSub) return;

    const prev = await fetchPrefsMaps(prevSub.id);

    const [savedOla, savedPhase, savedTask] = await Promise.all([
      Promise.all(
        Object.values(prev.olaPrefsMap)
          .filter(p => p.passionScore != null || p.expertiseFit != null)
          .map(p => api.createOlaPreference({
            olaId: p.olaId,
            hoewistSubmissionId: sub.id,
            collegaId,
            passionScore: p.passionScore ?? null,
            expertiseFit: p.expertiseFit ?? null,
            comment: p.comment ?? null,
          }).catch(() => null))
      ),
      Promise.all(
        Object.values(prev.phasePrefsMap)
          .filter(p => p.passionScore != null)
          .map(p => api.createPhasePreference({
            phaseId: p.phaseId,
            hoewistSubmissionId: sub.id,
            passionScore: p.passionScore,
            comment: p.comment ?? null,
          }).catch(() => null))
      ),
      Promise.all(
        Object.values(prev.taskPrefsMap)
          .filter(p => p.wantsToDo != null)
          .map(p => api.createTaskPreference({
            taskId: p.taskId,
            hoewistSubmissionId: sub.id,
            wantsToDo: p.wantsToDo,
            comment: p.comment ?? null,
          }).catch(() => null))
      ),
    ]);

    setOlaPrefs(Object.fromEntries(savedOla.filter(Boolean).map(p => [p.olaId, p])));
    setPhasePrefs(Object.fromEntries(savedPhase.filter(Boolean).map(p => [p.phaseId, p])));
    setTaskPrefs(Object.fromEntries(savedTask.filter(Boolean).map(p => [p.taskId, p])));
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [me, assignments, allOLAsFull, opos, allProgrammes, myTaskAssignments, allTasks, academicYearsData, globalSettings] =
        await Promise.all([
          api.getMyself(),
          api.getMyAssignments(),
          api.getOLAs(),
          api.getOPOs(),
          api.getProgrammes(),
          api.getMyTaskAssignments().catch(() => []),
          api.getTasks().catch(() => []),
          api.getAcademicYears().catch(() => []),
          api.getGlobalSettings().catch(() => null),
        ]);

      setHoewistOpen(globalSettings?.hoewistOpen ?? false);
      setTeacher(me);

      const opoProgrammeMap = {};
      const opoNameMap = {};
      (opos || []).forEach(opo => {
        if (opo.ID != null && opo.programmeID != null)
          opoProgrammeMap[opo.ID] = opo.programmeID;
        if (opo.ID != null)
          opoNameMap[opo.ID] = opo.name;
      });

      const assignedPhasesByOla = {};
      (assignments || []).forEach(a => {
        const olaId = a.olaID ?? a.ola?.ID;
        if (!olaId) return;
        if (!assignedPhasesByOla[olaId]) assignedPhasesByOla[olaId] = new Set();
        if (a.phaseID) assignedPhasesByOla[olaId].add(a.phaseID);
      });

      const assignedOlaIDs = new Set(Object.keys(assignedPhasesByOla).map(id => parseInt(id, 10)));

      const teacherProgrammeIDs = (me.programmes || [])
        .map(cp => cp.programmeID)
        .filter(Boolean);
      const teacherProgrammeIDSet = new Set(teacherProgrammeIDs);

      const safeOLAs = (allOLAsFull || [])
        .filter(ola => {
          const programmeID = opoProgrammeMap[ola.opoID];
          return programmeID && teacherProgrammeIDSet.has(programmeID);
        })
        .map(ola => {
          const isAssigned = assignedOlaIDs.has(ola.ID);
          const assignedPhaseIds = assignedPhasesByOla[ola.ID] || new Set();

          const phasesSeen = new Set();
          const phases = (ola.phases || []).reduce((acc, op) => {
            const pid = op.phaseID ?? op.phase?.ID;
            if (!pid) return acc;
            if (isAssigned && assignedPhaseIds.size > 0 && !assignedPhaseIds.has(pid)) return acc;
            if (phasesSeen.has(pid)) return acc;
            phasesSeen.add(pid);
            acc.push({
              phaseID: pid,
              phaseName: op.phase?.name ?? null,
              trajectoryName: op.phase?.trajectory?.name ?? null,
              campusName: op.phase?.campus?.name ?? null,
            });
            return acc;
          }, []);

          return {
            ID: ola.ID,
            name: ola.name,
            opoName: opoNameMap[ola.opoID] ?? null,
            semester: ola.semester,
            internship: ola.internship ?? false,
            programmeID: opoProgrammeMap[ola.opoID] ?? null,
            phases,
            isAssigned,
          };
        });
      setAllOLAs(safeOLAs);

      const teacherProgrammes = (allProgrammes || [])
        .filter(p => teacherProgrammeIDs.includes(p.ID))
        .sort((a, b) => a.name.localeCompare(b.name, "nl"));

      setProgrammes(teacherProgrammes);

      const allTasksMap = Object.fromEntries((allTasks || []).map(t => [t.ID, t]));

      const relevantTaskAssignments = (myTaskAssignments || []).filter(ta => {
        const progId = ta.programmeID ?? ta.programme?.ID;
        return progId == null || teacherProgrammeIDSet.has(progId);
      });

      const taskAssignedProgrammeIDs = {};
      relevantTaskAssignments.forEach(ta => {
        const taskId = ta.task?.ID ?? ta.taskID;
        const progId = ta.programmeID ?? ta.programme?.ID;
        if (!taskId) return;
        if (!taskAssignedProgrammeIDs[taskId]) taskAssignedProgrammeIDs[taskId] = new Set();
        if (progId) taskAssignedProgrammeIDs[taskId].add(progId);
      });

      const currentTasks = relevantTaskAssignments
        .map(ta => ta.task)
        .filter(Boolean)
        .filter((t, i, arr) => arr.findIndex(x => x.ID === t.ID) === i)
        .map(t => {
          const fullTask = allTasksMap[t.ID] ?? t;
          const assignedIDs = taskAssignedProgrammeIDs[t.ID];
          const filteredProgrammes = assignedIDs
            ? (fullTask.programmes || []).filter(p => assignedIDs.has(p.ID ?? p.programmeID))
            : fullTask.programmes;
          return { ...fullTask, programmes: filteredProgrammes };
        })
        .sort((a, b) => (a.name || "").localeCompare(b.name || "", "nl"));
      setMyTasks(currentTasks);

      const currentTaskIDs = new Set(currentTasks.map(t => t.ID));
      const availableNewTasks = (allTasks || [])
        .filter(t =>
          !currentTaskIDs.has(t.ID) &&
          t.isTeacherSelectable === true &&
          (t.programmes || []).some(p => teacherProgrammeIDSet.has(p.ID ?? p.programmeID))
        )
        .sort((a, b) => (a.name || "").localeCompare(b.name || "", "nl"));
      setNewTasks(availableNewTasks);

      const sortedYears = (academicYearsData || []).sort((a, b) => b.year.localeCompare(a.year));
      setAcademicYears(sortedYears);
      const currentYear = sortedYears.find(y => y.current_year);

      if (!currentYear) {
        setNoAcademicYear(true);
        return;
      }

      let subs = [];
      try {
        const fetched = await api.getHoewistSubmissionsByCollegaId(me.ID);
        subs = Array.isArray(fetched) ? fetched : [];
      } catch (_) { }

      let sub = subs.find(s => s.academicYearId === currentYear.id) ?? null;
      const isNewSubmission = !sub;
      if (!sub) {
        sub = await api.createHoewistSubmission({ collegaId: me.ID });
        subs = [...subs, sub];
      }
      setAllSubmissions(subs);
      setSelectedSubmissionId(sub.id);
      setSubmission(sub);

      const existing = await fetchPrefsMaps(sub.id);
      const hasExisting =
        Object.keys(existing.olaPrefsMap).length > 0 ||
        Object.keys(existing.phasePrefsMap).length > 0 ||
        Object.keys(existing.taskPrefsMap).length > 0;

      if (isNewSubmission && !hasExisting) {
        await prefillFromPreviousSubmission(sub, subs, sortedYears, me.ID);
      } else {
        setOlaPrefs(existing.olaPrefsMap);
        setPhasePrefs(existing.phasePrefsMap);
        setTaskPrefs(existing.taskPrefsMap);
      }
    } catch (error) {
      toastService.send({ title: "Fout bij laden", message: error.toString() });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const updateOlaPref = (olaId, updates) => {
    setOlaPrefs(prev => ({
      ...prev,
      [olaId]: { ...(prev[olaId] || { olaId }), ...updates },
    }));
    setUnsaved(true);
  };

  const updatePhasePref = (phaseId, updates) => {
    setPhasePrefs(prev => ({
      ...prev,
      [phaseId]: { ...(prev[phaseId] || { phaseId }), ...updates },
    }));
    setUnsaved(true);
  };

  const updateTaskPref = (taskId, updates) => {
    setTaskPrefs(prev => ({
      ...prev,
      [taskId]: { ...(prev[taskId] || { taskId }), ...updates },
    }));
    setUnsaved(true);
  };

  const switchYear = async (subId) => {
    const targetSub = allSubmissions.find(s => s.id === parseInt(subId, 10));
    if (!targetSub) return;

    setLoading(true);
    setSelectedSubmissionId(targetSub.id);
    setSubmission(targetSub);
    setUnsaved(false);

    try {
      const { olaPrefsMap, phasePrefsMap, taskPrefsMap } = await fetchPrefsMaps(targetSub.id);
      setOlaPrefs(olaPrefsMap);
      setPhasePrefs(phasePrefsMap);
      setTaskPrefs(taskPrefsMap);
    } catch (error) {
      toastService.send({ title: "Fout bij laden", message: error.toString() });
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    if (!submission?.academicYearId) {
      toastService.send({ title: "Opslaan niet mogelijk", message: "Voorkeuren kunnen momenteel niet worden opgeslagen. Probeer later opnieuw." });
      return;
    }
    setSaving(true);
    try {
      await Promise.all([
        ...Object.entries(olaPrefs)
          .filter(([, pref]) => pref.passionScore != null || pref.expertiseFit != null)
          .map(([olaId, pref]) => upsertPref(
            pref,
            {
              olaId: parseInt(olaId, 10),
              hoewistSubmissionId: submission.id,
              collegaId: teacher.ID,
              passionScore: pref.passionScore ?? null,
              expertiseFit: pref.expertiseFit ?? null,
              comment: pref.comment ?? null,
            },
            api.createOlaPreference,
            api.updateOlaPreference,
            setOlaPrefs,
            olaId,
          )),

        ...Object.entries(phasePrefs)
          .filter(([, pref]) => pref.passionScore != null)
          .map(([phaseId, pref]) => upsertPref(
            pref,
            {
              phaseId: parseInt(phaseId, 10),
              hoewistSubmissionId: submission.id,
              passionScore: pref.passionScore,
              comment: pref.comment ?? null,
            },
            api.createPhasePreference,
            api.updatePhasePreference,
            setPhasePrefs,
            phaseId,
          )),

        ...Object.entries(taskPrefs)
          .map(async ([taskId, pref]) => {
            if (pref.wantsToDo == null && !pref.comment) {
              if (pref.id) {
                await api.deleteTaskPreference(pref.id).catch(() => {});
                setTaskPrefs(prev => { const n = { ...prev }; delete n[taskId]; return n; });
              }
              return;
            }
            if (pref.wantsToDo == null) return;
            await upsertPref(
              pref,
              {
                taskId: parseInt(taskId, 10),
                hoewistSubmissionId: submission.id,
                wantsToDo: pref.wantsToDo,
                comment: pref.comment ?? null,
              },
              api.createTaskPreference,
              api.updateTaskPreference,
              setTaskPrefs,
              taskId,
            );
          }),
      ]);

      await api.updateHoewistSubmission(submission.id, {
        id: submission.id,
        collegaId: teacher.ID,
        submittedAt: new Date().toISOString(),
      });

      setUnsaved(false);
      toastService.send({ title: "Opgeslagen", message: "Uw voorkeuren zijn bewaard." });
    } catch (error) {
      toastService.send({ title: "Fout bij opslaan", message: error.toString() });
    } finally {
      setSaving(false);
    }
  };

  return {
    loading, saving,
    teacher, programmes, allOLAs, myTasks, newTasks,
    submission, allSubmissions, selectedSubmissionId, academicYears,
    noAcademicYear, hoewistOpen,
    olaPrefs, phasePrefs, taskPrefs,
    unsaved,
    updateOlaPref, updatePhasePref, updateTaskPref,
    switchYear, save,
  };
}
